import * as Yup from 'yup';
import { useFormik } from 'formik';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Seo } from 'src/components/seo';
import { useMounted } from 'src/hooks/use-mounted';
import {
  setPasswordAction,
  resetSetPassword,
  resetTherapistLogin,
} from 'src/redux/actions/authActions';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from 'src/paths';

interface Values {
  oldPassword: string;
  password: string;
  passwordConfirm: string;
  submit: null;
}

const getInitialValues = (): Values => ({
  oldPassword: '',
  password: '',
  passwordConfirm: '',
  submit: null,
});

const passwordRules = [
  /(?=.*[A-Z])/,
  /(?=.*[a-z])/,
  /(?=.*\d)/,
  /(?=.*[!@#$%^&*])/,
  /^[^\s]{8,16}$/,
];

const passwordValidationSchema = Yup.string()
  .required('Please enter a valid New Password')
  .test('password-validation', 'Password should contain at least one uppercase letter.', (value) =>
    passwordRules[0].test(value)
  )
  .test('password-validation', 'Password should contain at least one lowercase letter', (value) =>
    passwordRules[1].test(value)
  )
  .test('password-validation', 'Password should contain at least one numeric digit', (value) =>
    passwordRules[2].test(value)
  )
  .test(
    'password-validation',
    'Password should contain at least one special character (!@#$%^&*)',
    (value) => passwordRules[3].test(value)
  )
  .test(
    'password-validation',
    'Password must be at least 8 characters long and cannot exceed 16 characters',
    (value) => passwordRules[4].test(value)
  );

const validationSchema = Yup.object({
  oldPassword: Yup.string().required('Please enter a valid Current Password'),
  password: passwordValidationSchema,
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref('password')], 'New Password and Confirm Password should match')
    .required('Please enter a valid Confirm Password'),
});

const Page = () => {
  const isMounted = useMounted();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const checkLoginResponse = useSelector((state: any) => state.therapistLoginReducer);
  const checkSetPasswordResponse = useSelector((state: any) => state.setPasswordReducer);
  const [error, setError] = useState('');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        dispatch(
          setPasswordAction({
            newPassword: values.password,
            oldPassword: values.oldPassword,
            linkId: '',
            uuid: '',
            enterpriseId: '',
            self: true,
            token: checkLoginResponse?.response?.data?.accessToken,
          })
        );
      } catch (err) {
        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  useEffect(() => {
    if (checkSetPasswordResponse.isSetPassword) {
      dispatch(resetTherapistLogin());
      dispatch(resetSetPassword());
      navigate(paths.auth.login);
    } else {
      if (checkSetPasswordResponse.response !== null) {
        setError(
          checkSetPasswordResponse.response?.message || checkSetPasswordResponse.response?.error
        );
      }
    }
  }, [checkSetPasswordResponse]);

  return (
    <>
      <Seo title="Reset Password" />
      <div>
        <Card elevation={16}>
          <CardHeader
            sx={{ pb: 0 }}
            title="Reset Password"
          />
          <CardContent>
            <form
              noValidate
              onSubmit={formik.handleSubmit}
            >
              <Stack spacing={3}>
                <TextField
                  error={!!(formik.touched.oldPassword && formik.errors.oldPassword)}
                  fullWidth
                  helperText={formik.touched.oldPassword && formik.errors.oldPassword}
                  label="Current Password"
                  name="oldPassword"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const trimmedValue = e.target.value.trim();
                    formik.setFieldValue('oldPassword', trimmedValue);
                  }}
                  value={formik.values.oldPassword}
                />
                <TextField
                  error={!!(formik.touched.password && formik.errors.password)}
                  fullWidth
                  helperText={formik.touched.password && formik.errors.password}
                  label="New Password"
                  name="password"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const trimmedValue = e.target.value.trim();
                    formik.setFieldValue('password', trimmedValue);
                  }}
                  type="password"
                  value={formik.values.password}
                />
                <TextField
                  error={!!(formik.touched.passwordConfirm && formik.errors.passwordConfirm)}
                  fullWidth
                  helperText={formik.touched.passwordConfirm && formik.errors.passwordConfirm}
                  label="Confirm Password"
                  name="passwordConfirm"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const trimmedValue = e.target.value.trim();
                    formik.setFieldValue('passwordConfirm', trimmedValue);
                  }}
                  type="password"
                  value={formik.values.passwordConfirm}
                />
              </Stack>
              {error && (
                <FormHelperText
                  error
                  sx={{ mt: 3 }}
                >
                  {error as string}
                </FormHelperText>
              )}
              <Button
                disabled={formik.isSubmitting}
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                type="submit"
                variant="contained"
              >
                Reset Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Page;
